import { useState, useMemo } from "react";
import { useTranslate } from "@refinedev/core";
import { usePaginatedTable, useQueryParams } from "@shared/hooks";
import { useFetchResourcesByIdsQuery } from "@shared/services/queries";
import { ApiResponse, Discount } from "@shared/types";
import { User } from "@modules/users/types";
import { Agreement } from "@modules/agreements/types";
import { Company } from "@modules/companies/types";
import { Usage } from "@modules/usages/types";
import { resolveUsageDateRange } from "@modules/usages/utils/period";

const ANALYTICS_FILTER_KEYS = [
  "kind",
  "created_at_after",
  "created_at_before",
  "company",
  "agreement",
  "user",
  "city",
  "state",
  "discount",
  "discount__mode",
  "discount__type",
  "payment_status",
] as const;

function pickAnalyticsFilters(filters: Record<string, string>) {
  return Object.fromEntries(
    ANALYTICS_FILTER_KEYS.filter((key) => filters[key]).map((key) => [key, filters[key]])
  );
}

export function useUsagesList() {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const translate = useTranslate();
  const { getQueryParams } = useQueryParams();
  const queryParams = getQueryParams();
  const kind = queryParams.kind || queryParams.tab || "cinema";
  const { tab: _tab, period: _period, ...restParams } = queryParams;
  const filters = { ...restParams, kind, ...resolveUsageDateRange(queryParams) };

  const { rows, loading, refetch, ...tableProps } = usePaginatedTable<Usage>({
    resource: "v1/usage_admin",
    filters,
    sorters: { ordering: "-created_at" },
  });

  const relatedIds = useMemo(() => ({
    companies: [...new Set(rows?.map((usage) => usage.company).filter((id): id is number => !!id))],
    agreements: [...new Set(rows?.map((usage) => usage.agreement).filter((id): id is number => !!id))],
    users: [...new Set(rows?.map((usage) => usage.user).filter((id): id is number => !!id))],
    discounts: [...new Set(rows?.map((usage) => usage.discount).filter((id): id is number => !!id))],
  }), [rows]);

  const { data: companies } = useFetchResourcesByIdsQuery<ApiResponse<Company>>({
    resource: "v1/company_admin",
    ids: relatedIds.companies,
  });

  const { data: agreements } = useFetchResourcesByIdsQuery<ApiResponse<Agreement>>({
    resource: "v1/agreement_admin",
    ids: relatedIds.agreements,
  });

  const { data: users } = useFetchResourcesByIdsQuery<ApiResponse<User>>({
    resource: "v1/user_admin",
    ids: relatedIds.users,
  });

  const { data: discounts } = useFetchResourcesByIdsQuery<ApiResponse<Discount>>({
    resource: "v1/discount_admin",
    ids: relatedIds.discounts,
  });

  const dataToExport = useMemo(() => (
    selectedRows.length > 0 ? rows.filter((row) => selectedRows.includes(row.id)) : rows
  ), [selectedRows, rows]);

  return {
    rows,
    loading,
    tableProps,
    selectedRows,
    dataToExport,
    companies: companies?.results,
    agreements: agreements?.results,
    users: users?.results,
    discounts: discounts?.results,
    refetch,
    setSelectedRows,
    translate,
    filters,
    analyticsFilters: pickAnalyticsFilters(filters),
    kind,
  };
}
